// What an ask_user_question call carries, and what an answer becomes.
// Leaf on purpose: no React, so the thread chassis can key the tool out of the
// collapsed tool group on the name alone (ui/components/assistant-ui/thread.tsx).

/** Mirrors ASK_USER_QUESTION in src/ask-user-question.mjs. */
export const ASK_USER_QUESTION = 'ask_user_question';

export interface AskedOption {
  label: string;
  description?: string;
  /** The screenshotId of an app_screenshot in this thread: the option's picture. */
  previewImage?: string;
}
export interface AskedQuestion {
  header: string;
  question: string;
  options: AskedOption[];
  multiSelect?: boolean;
}
/** One question's working answer: options ticked, plus anything typed. */
export interface QuestionDraft {
  picked: string[];
  other: string;
  otherPicked: boolean;
}

export const emptyDraft = (): QuestionDraft => ({ picked: [], other: '', otherPicked: false });

/** Whether a message part is an ask the user can answer. A call the model had
 * to retry (its input failed validation) stays in the message without
 * questions; every reader of the open ask must skip it the same way. */
export function hasAskedQuestions(part: { type: string; toolName?: string; result?: unknown }) {
  if (part.type !== 'tool-call' || part.toolName !== ASK_USER_QUESTION) return false;
  const questions = (part.result as { questions?: unknown } | null | undefined)?.questions;
  return Array.isArray(questions) && questions.length > 0;
}

/** The client owns the escape hatch, and its value is the typed text — never
 * the words on the row. The model is told not to write its own "Other". */
export function answerValue(draft: QuestionDraft | undefined) {
  const typed = (draft?.other ?? '').trim();
  // Changing a selected option must not silently discard the person's words.
  // Skip clears the draft explicitly; only empty text is omitted here.
  return [...(draft?.picked ?? []), typed].filter(Boolean).join(', ');
}

export const isAnswered = (draft: QuestionDraft | undefined) => answerValue(draft).length > 0;

/** What a question the user had no answer for says in their message. Kept in
 * their voice — it appears in their own bubble — and stated rather than left
 * out: a question that is simply missing reads as unanswered, and the model
 * asks it again. */
export const NO_PREFERENCE = 'no preference';

/**
 * The ordinary user message a submitted panel sends: one line per question,
 * in the order they were asked.
 *
 * Every question is reported, including the ones skipped, because the index
 * only ever moves one step and Submit exists only on the last question — so by
 * the time this runs the user has seen them all, and silence about one of them
 * would be the model's cue to ask it again rather than pick a default.
 * An empty result means nothing at all was answered; the panel sends
 * SKIP_ANSWER instead, which says the same thing in one sentence.
 */
export function composeQuestionAnswer(
  questions: AskedQuestion[],
  drafts: QuestionDraft[],
  noPreference = NO_PREFERENCE,
) {
  const answers = questions.map((question, index) => ({
    header: question.header,
    value: answerValue(drafts[index]),
  }));
  if (!answers.some(({ value }) => value)) return '';
  return answers.map(({ header, value }) => `${header}: ${value || noPreference}`).join('\n');
}

/** Skipping everything is still a turn: it tells the model to proceed. */
export const SKIP_ANSWER =
  'Skip these questions. Continue with your best assumption and say what you assumed.';
